import { IProductHistoryEvent } from "@/common/types";
import {
  formatChangeValue,
  formatEventDate,
} from "@/common/utils/product-history";
import { EditEntryItem } from "../style";

export interface IEditEntryProps {
  event: IProductHistoryEvent;
}

/** MED-682 — one edit: when it happened, who made it, and what it changed. */
export function EditEntry({ event }: IEditEntryProps) {
  const { actor } = event;
  const changes = event.changed_fields ?? [];

  // MED-682: the person, not the company. MED-678's `display_name` leads with
  // the submitting company, which is right for the admin panel — a reviewer
  // cares which company submitted — but every row here belongs to the
  // manufacturer's own company, so the company is the constant and the person is
  // the variable. Telling apart several users on one company account is the
  // reason this panel exists.
  //
  // `display_name` is the fallback rather than the default, because it carries
  // the backend's wording for an event with no acting user ("Not recorded"),
  // which must not be re-invented here. An edit almost always has a person:
  // `record_resubmission` passes `current_user`, and `actor_name` itself falls
  // back to that user's email where their name columns are blank.
  const actorName = actor?.name || actor?.display_name || "";
  const unattributed = actor ? !actor.attributed : true;

  return (
    <EditEntryItem>
      <div className="entry-head">
        <span className="entry-date">{formatEventDate(event.occurred_at)}</span>
        <span className="entry-actor">
          {/* "by Not recorded" reads as a name; an edit with no actor states
              the absence on its own instead. */}
          {unattributed ? null : "by "}
          <span className={unattributed ? "actor-unattributed" : "actor-name"}>
            {actorName}
          </span>
        </span>
      </div>

      {changes.length > 0 ? (
        <div className="entry-changes">
          {changes.map((change) => (
            <div className="change-row" key={change.field}>
              <span className="change-field">{change.label}</span>
              <span className="change-from">{formatChangeValue(change.from)}</span>
              <span className="change-arrow" aria-hidden="true">
                &rarr;
              </span>
              <span className="change-to">{formatChangeValue(change.to)}</span>
            </div>
          ))}
        </div>
      ) : (
        // The backend refuses an edit that changes nothing, so this is only
        // reachable for a row recorded before that guard existed. Saying so is
        // better than rendering an entry with a blank body.
        <span className="entry-no-changes">
          No field-level detail was recorded for this edit.
        </span>
      )}
    </EditEntryItem>
  );
}
